import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getRelatedTopics, getThemeForTopic } from '../data';
import { useReadingProgress } from '../hooks/useReadingProgress';
import ReadingProgressBar from './ReadingProgressBar';
import ReadingProgressMeta from './ReadingProgressMeta';
import ReadingProgressRing from './ReadingProgressRing';
import TopicBlocks from './TopicBlocks';
import TopicTagList from './TopicTagList';
import type { TopicContent, TopicMetadata } from '../types';

interface TopicContentRendererProps {
    topic: TopicMetadata;
    content: TopicContent;
}

export default function TopicContentRenderer({ topic, content }: TopicContentRendererProps) {
    const shouldReduceMotion = useReducedMotion();
    const relatedTopics = getRelatedTopics(topic.slug);
    const { contentRef, scrollPercent, completed, toggleCompleted } = useReadingProgress(topic);
    const displayPercent = completed ? 100 : scrollPercent;

    return (
        <motion.div
            key={topic.slug}
            className="min-h-screen"
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.2, ease: 'easeOut' }}
        >
            <ReadingProgressBar percent={displayPercent} />
            <ReadingProgressRing percent={displayPercent} />

            <div className="mx-auto max-w-7xl px-6 py-16">
                <div ref={contentRef}>
                    <h1
                        className="mb-6 text-center text-4xl font-bold text-darkBlue dark:text-lightBlue md:text-5xl"
                        style={{ lineHeight: 1.55 }}
                    >
                        {topic.title}
                    </h1>

                    <TopicTagList tags={topic.tags} className="mb-6 justify-center" />

                    <ReadingProgressMeta
                        topic={topic}
                        scrollPercent={displayPercent}
                        completed={completed}
                        onToggleCompleted={toggleCompleted}
                    />

                    <div className="mb-6 border-b-2 border-gray-300 pb-4 dark:border-gray-700" />

                    <TopicBlocks content={content} />

                    <div className="mt-8 border-t-2 border-gray-300 pt-8 dark:border-gray-700" />
                </div>

                {relatedTopics.length > 0 && (
                    <div className="mt-0">
                        <h2 className="mb-6 text-2xl font-bold text-lightOrange" style={{ fontSize: '21px', lineHeight: 1.5 }}>
                            Related Topics
                        </h2>
                        <ul className="space-y-3">
                            {relatedTopics.map((relatedTopic) => {
                                const relatedTheme = getThemeForTopic(relatedTopic.slug);
                                if (!relatedTheme) {
                                    return null;
                                }

                                return (
                                    <li key={relatedTopic.slug}>
                                        <Link
                                            to={`/thematic-studies/${relatedTheme.slug}/${relatedTopic.slug}`}
                                            className="font-medium text-darkBlue transition-colors hover:underline dark:text-lightBlue"
                                            style={{ fontSize: '19px' }}
                                        >
                                            {relatedTopic.title}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
